import { useSearchOpcs4Codes } from "@/clinical-codes/useSearchOpcs4Codes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useDebounce } from "@/hooks/use-debounce";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { Opcs4CodeRule, UpsertOpcs4RuleData } from "./useOpcs4CodeRules";
import type { UseMutationResult } from "@tanstack/react-query";

interface UpsertOpcs4CodeRuleProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule?: Opcs4CodeRule | null;
  onSuccess?: () => void;
  upsertRule?: UseMutationResult<
    Opcs4CodeRule | null,
    Error,
    { ruleData: UpsertOpcs4RuleData; ruleId?: string },
    unknown
  >;
}

export const UpsertOpcs4CodeRule = ({ open, onOpenChange, rule, onSuccess, upsertRule }: UpsertOpcs4CodeRuleProps) => {
  const _queryClient = useQueryClient();
  const isEditing = !!rule;

  // Form state
  const [name, setName] = useState(rule?.name || "");
  const [ruleText, setRuleText] = useState(rule?.rule_text || "");
  const [isActive, setIsActive] = useState(rule?.is_active ?? true);
  const [selectedCodes, setSelectedCodes] = useState<
    Array<{
      id: string;
      code: string;
      description: string;
    }>
  >([]);

  // Update state if the rule prop changes
  useEffect(() => {
    if (rule) {
      setName(rule.name);
      setRuleText(rule.rule_text);
      setIsActive(rule.is_active);
      setSelectedCodes(
        rule.opcs4CodeRuleAssignments.map((assignment) => ({
          id: assignment.opcs4Code.id,
          code: assignment.opcs4Code.code,
          description: assignment.opcs4Code.description || "",
        })),
      );
    }
  }, [rule]);

  // OPCS-4 code search
  const [codeSearch, setCodeSearch] = useState("");
  const debouncedCodeSearch = useDebounce(codeSearch, 300);
  const { codes: searchResults, isLoading: isSearching } = useSearchOpcs4Codes(debouncedCodeSearch);

  const handleClose = () => {
    setName("");
    setRuleText("");
    setIsActive(true);
    setSelectedCodes([]);
    setCodeSearch("");
    onOpenChange(false);
  };

  const handleAddCode = (code: { id: string; code: string; description: string }) => {
    if (!selectedCodes.find((c) => c.id === code.id)) {
      setSelectedCodes((prev) => [...prev, code]);
    }
    setCodeSearch("");
  };

  const handleRemoveCode = (codeId: string) => {
    setSelectedCodes((prev) => prev.filter((c) => c.id !== codeId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!upsertRule) {
      toast.error("Upsert function not available");
      return;
    }

    try {
      await upsertRule.mutateAsync({
        ruleData: {
          name,
          rule_text: ruleText,
          is_active: isActive,
          codeIds: selectedCodes.map((code) => code.id),
        },
        ruleId: rule?.id,
      });

      // Close dialog and call success callback
      onSuccess?.();
      handleClose();
    } catch (error) {
      // Error is already handled by the mutation
      console.error("Error upserting rule:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit OPCS-4 Code Rule" : "Create New OPCS-4 Code Rule"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the rule details and associated OPCS-4 codes."
              : "Create a new rule and assign OPCS-4 codes to it."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            {/* Rule Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Rule Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter rule name"
                required
              />
            </div>

            {/* Rule Text */}
            <div className="space-y-2">
              <Label htmlFor="ruleText">Rule Text *</Label>
              <Textarea
                id="ruleText"
                value={ruleText}
                onChange={(e) => setRuleText(e.target.value)}
                placeholder="Enter rule description or logic"
                rows={4}
                required
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center space-x-2">
              <Switch id="isActive" checked={isActive} onCheckedChange={setIsActive} />
              <Label htmlFor="isActive">Active</Label>
            </div>

            {/* OPCS-4 Code Selection */}
            <div className="space-y-4">
              <Label>Associated OPCS-4 Codes *</Label>

              {/* Selected Codes Display */}
              {selectedCodes.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Selected Codes ({selectedCodes.length})</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedCodes.map((code) => (
                      <Badge key={code.id} variant="secondary" className="flex items-center gap-1">
                        <span className="font-mono">{code.code}</span>
                        <span className="max-w-[200px] truncate">{code.description}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1"
                          onClick={() => handleRemoveCode(code.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Code Search */}
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={codeSearch}
                    onChange={(e) => setCodeSearch(e.target.value)}
                    placeholder="Search OPCS-4 codes..."
                    className="pl-10"
                  />
                </div>

                {/* Search Results */}
                {codeSearch && (
                  <ScrollArea className="h-48 border rounded-md">
                    <div className="p-2 space-y-1">
                      {isSearching ? (
                        <div className="text-sm text-muted-foreground p-2">Searching...</div>
                      ) : searchResults && searchResults.length > 0 ? (
                        searchResults
                          .filter((code) => !selectedCodes.find((selected) => selected.id === code.id))
                          .slice(0, 20) // Limit results for performance
                          .map((code) => (
                            <Button
                              key={code.id}
                              type="button"
                              variant="ghost"
                              className="w-full justify-start h-auto p-2 text-left"
                              onClick={() => handleAddCode(code)}
                            >
                              <div className="flex items-center gap-2">
                                <Plus className="h-3 w-3" />
                                <span className="font-mono">{code.code}</span>
                                <span className="text-muted-foreground truncate">{code.description}</span>
                              </div>
                            </Button>
                          ))
                      ) : (
                        <div className="text-sm text-muted-foreground p-2">No codes found</div>
                      )}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={upsertRule?.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={upsertRule?.isPending || !name.trim() || !ruleText.trim() || selectedCodes.length === 0}
            >
              {upsertRule?.isPending
                ? isEditing
                  ? "Updating..."
                  : "Creating..."
                : isEditing
                  ? "Update Rule"
                  : "Create Rule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
